<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { computed, ComputedRef, ref, toRefs, watch } from 'vue';
import { SubscriptionTypeFilter, useContentByOriginalTitle } from '@ilteducation/content-search';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import { ContentTypes, MaterialChip, MaterialDetails, useFileActions } from '@ilteducation/components';
import { v4 as uuidv4 } from 'uuid';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useMaterialsStore } from '@ilteducation/content';
import MaterialDetailsDrawer from '@/components/material/MaterialDetailsDrawer.vue';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { useMyOrganizations } from '@ilteducation/my-account';
import { detect } from 'detect-browser';
import useLanguagesStore from '@/store/languages';
import type { NewEvent } from '@ilteducation/activity-stream-types';
import { sendEvents } from '@ilteducation/activity-stream-events';
import type { Category, Area } from '@ilteducation/content-ref-data-types/models/data-types';
import { useContentRefData } from '@ilteducation/content-ref-data';
import { storeToRefs } from 'pinia';
import { useLocale } from '@/plugins/translation';
import useGuiStore from '@/store/gui';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useAppVersion from '@/composables/use-app-version';
import { DialogCloseButton } from '@ilteducation/components';
import { CollectionSelectOwnDialog, CollectionEditOwnDialog } from '@ilteducation/content-collections';
import config from '@/config/ConfigProvider';
import { useAddToCollection } from '@/composables/use-add-to-collection';
import useMaterialCategories from '@/composables/use-material-categories';

const props = defineProps<{
  modelValue: boolean;
  selectedMaterial: MaterialV1;
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:selected-material', material: MaterialV1): void;
}>();

const { modelValue } = toRefs(props);
const localModel = ref(modelValue.value);
const justSwitchedMaterial = ref(false);

watch(modelValue, (val) => {
  localModel.value = val;
});

watch(localModel, (val) => {
  // Even if we just switched language, we still want to reset the fileUrl
  fileUrl.value = '';
  if (!val && justSwitchedMaterial.value) {
    // Vuetify tried to close, but we just switched language.
    localModel.value = true;
    justSwitchedMaterial.value = false;
    return;
  }
  emits('update:modelValue', val);
});

const handleClose = () => {
  fileUrl.value = '';
  justSwitchedMaterial.value = false;
  localModel.value = false;
};

const { t } = useLocale();

const subscriptions = useSubscriptions();
const partitionKey = uuidv4();
const userContext = useUserContext();
const { organizations } = useMyOrganizations(true);
const browser = detect();
const { getMarketLanguage } = useLanguagesStore();
const materialsStore = useMaterialsStore();
const { getContentDownloadUrl } = materialsStore;
const { downloadFile, printFile } = useFileActions();

const contentRefDataStore = useContentRefData();
const { byId } = storeToRefs(contentRefDataStore);
const { getThemeName } = useGuiStore();

const globalFilters = computed(() => SubscriptionTypeFilter(subscriptions.value[0].type));
const mat = computed(() => props.selectedMaterial);
const materialsWithSameOriginalTitle = useContentByOriginalTitle(mat, 'Material', globalFilters);

const isDownloadLoading = ref(false);
const isPrintLoading = ref(false);
const fileUrl = ref<string>('');
const { appVersion } = useAppVersion();

const clickDownload = async () => {
  isDownloadLoading.value = true;
  await logMethod('download');
  if (!fileUrl.value) {
    fileUrl.value = await getContentDownloadUrl(mat.value.id);
  }
  await downloadFile(fileUrl.value, mat.value.title);
  isDownloadLoading.value = false;
};

const clickPrint = async () => {
  isPrintLoading.value = true;
  await logMethod('print');
  if (!fileUrl.value) {
    fileUrl.value = await getContentDownloadUrl(mat.value.id);
  }
  await printFile(fileUrl.value);
  isPrintLoading.value = false;
};

const onLanguageChange = (newMaterial: MaterialV1) => {
  fileUrl.value = '';
  justSwitchedMaterial.value = true;
  emits('update:selected-material', newMaterial);
};

const categories: ComputedRef<Category[]> = computed(() =>
  (props.selectedMaterial.categories ?? [])
    .map((item) => byId.value('categories', item))
    .filter((category): category is Category => category !== undefined),
);
const areas: ComputedRef<Area[]> = computed(() =>
  (props.selectedMaterial.areas ?? [])
    .map((item) => byId.value('areas', item))
    .filter((area): area is Area => area !== undefined),
);

const gradeChips = computed(() => categories.value.filter((item: Category) => item.kind === 'grade'));
const { otherCategories } = useMaterialCategories(computed(() => props.selectedMaterial));
const subjectChips: ComputedRef<MaterialChip[]> = computed(() => {
  const values: MaterialChip[] = categories.value
    .filter((item: Category) => item.kind === 'subject')
    .map((item: Category) => ({
      icon: 'fa:fas fa-tag',
      title: item.category,
      theme: getThemeName(item.slug),
      colorClass: 'primary-container',
    }));
  areas.value.forEach((area: Area) => {
    values.push({
      icon: 'fa:fas fa-tag',
      title: area.name,
      theme: getThemeName(categories.value.find((item) => item.id === area.categoryId)?.slug || 'default'),
      colorClass: 'secondary-container',
    } as MaterialChip);
  });
  return values;
});

const logMethod = async (method: 'download' | 'print' | 'share') => {
  const event: NewEvent = {
    partitionKey: partitionKey,
    sourceApplication: 'manabua',
    type: 'ContentFileExportedEventV1',
    payload: {
      sessionId: partitionKey,
      timestamp: new Date().toISOString() as any,
      contentReference: { id: mat.value.id, type: 'Material', language: mat.value.language },
      userIdentity: {
        type: 'User',
        id: userContext.user.uid,
        roles: userContext.roles.map((role) => role.value),
        organisations: organizations.value.data.map((org: SlimOrganization) => org.uid),
      },
      deviceInfo: {
        type: 'Browser',
        name: browser?.name || 'unknown',
        version: browser?.version || '0.0.0',
      },
      appInfo: {
        type: 'Web',
        version: appVersion || '0',
        language: getMarketLanguage() || 'sv',
      },
      service: 'manabua',
      method: method,
    },
  };
  await sendEvents([event], true);
};

const { xs } = useDisplay();

const collection = useAddToCollection({
  contentReference: computed(() => ({
    id: mat.value?.id ?? '',
    type: ContentTypes.Material,
  })),
  context: config.value('context'),
  onUpdated: () => handleClose(),
  onCreated: () => handleClose(),
});
const { showSelectDialog, showCreateDialog, contentReference: materialReference } = collection;
</script>

<template>
  <MaterialDetailsDrawer
    v-if="xs && selectedMaterial"
    :material="selectedMaterial"
    :materials-with-same-original-title="materialsWithSameOriginalTitle as MaterialV1[]"
    :is-download-loading="isDownloadLoading"
    :is-print-loading="isPrintLoading"
    @click-download="clickDownload"
    @click-print="clickPrint"
    @click-add-to-collection="collection.openSelectDialog"
    @language-changed="onLanguageChange"
    v-model="localModel"
    @close="handleClose"
  ></MaterialDetailsDrawer>
  <v-dialog
    v-if="!xs"
    v-model="localModel"
    content-class="align-center"
    max-width="fit-content"
    :aria-label="selectedMaterial.title"
  >
    <v-card min-width="min(720px, 95vw)" max-width="940" class="rounded-lg material-dialog-container">
      <v-card-text class="pa-0">
        <DialogCloseButton @click="handleClose" />
        <MaterialDetails
          v-if="selectedMaterial"
          :material="selectedMaterial"
          :materials-with-same-original-title="materialsWithSameOriginalTitle as MaterialV1[]"
          :is-download-loading="isDownloadLoading"
          :is-print-loading="isPrintLoading"
          :use-padding="true"
          @click-download="clickDownload"
          @click-print="clickPrint"
          @click-add-to-collection="collection.openSelectDialog"
          @language-changed="onLanguageChange"
        >
          <template #audience-ages>
            <v-chip
              v-for="chip in gradeChips"
              :key="chip.categoryCode"
              class="mr-4 mb-4 rounded bg-primary-container"
              color="on-surface"
              label
              variant="flat"
              role="listitem"
            >
              <template #prepend>
                <FontAwesomeIcon class="mr-1" icon="fa-solid fa-graduation-cap"></FontAwesomeIcon>
              </template>
              {{ t(`grades.${chip.categoryCode}`) }}
            </v-chip>
          </template>
          <template v-if="otherCategories.length > 0" #categories>
            <v-chip
              v-for="category in otherCategories"
              :key="category.id"
              class="mr-4 mb-4 rounded bg-tertiary-container"
              color="on-tertiary"
              label
              prepend-icon="fa:fas fa-tag"
              variant="flat"
              role="listitem"
              tag="li"
            >
              {{ t(`${category.category}`) }}
            </v-chip>
          </template>
          <template v-if="subjectChips.length > 0" #after>
            <p class="mb-4 category-label">{{ t('label_subjects') }}</p>
            <ul class="d-flex flex-wrap pa-0 ma-0 chip-list" :aria-label="t('label_subjects')">
              <li v-for="chip in subjectChips" :key="chip.title" class="mr-4 mb-4">
                <v-theme-provider :theme="chip.theme">
                  <v-chip
                    class="rounded"
                    :prepend-icon="chip.icon"
                    :color="chip.colorClass"
                    label
                    variant="flat"
                    role="none"
                  >
                    {{ t(`${chip.title}`) }}
                  </v-chip>
                </v-theme-provider>
              </li>
            </ul>
          </template>
        </MaterialDetails>
      </v-card-text>
    </v-card>
  </v-dialog>
  <CollectionSelectOwnDialog
    v-if="showSelectDialog"
    :content-references="[materialReference]"
    :context="collection.context"
    :model-value="showSelectDialog"
    @close="collection.closeSelectDialog"
    @open-create-collection="collection.openCreateDialog"
    @update-collection="collection.handleUpdateCollection"
  />
  <CollectionEditOwnDialog
    v-if="showCreateDialog"
    :content-references="[materialReference]"
    :context="collection.context"
    :show-icons="false"
    selected-icon="thinPhotoFilm"
    @create-collection="collection.handleCreateCollection"
    @close="collection.closeCreateDialog"
  />
</template>

<style scoped lang="scss">
.material-dialog-container {
  border: 1px solid rgb(var(--v-theme-outline-variant));
}
.category-label {
  font-size: 0.75rem;
  font-weight: 500;
}
.chip-list {
  list-style: none;
}
</style>
